/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { Helmet } from "react-helmet-async";
import Container from "../../../../components/Container/Container";
import Layout from "../../../Layouts/Layout";
import React, { useEffect, useState } from "react";
import HintBox from "../../../../components/Box/HintBox/HintBox";
import FileUploadSection from "../../../../components/Form/Common/FileUploadSection/FileUploadSection";
import LegalbirdLoader from "../../../../components/ContentLoader/LegalbirdLoader";
import TextContainer from "../../../../components/Container/TextContainer";
import _map from "lodash/map";
import _forEach from "lodash/forEach";
import ButtonLoading from "../../../../components/Button/ButtonLoading";
import requestDocumentsPageStyle from "./requestDocumentsPageStyle";
import Paper from "@mui/material/Paper";
import ApiClient from "../../../../services/apiClient";
import { useNavigate } from "react-router";
import { getCaseLink, getProductData } from "../../../../services/productService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys } from "../../../../services/reactQuery/reactQueryService";
import useProductParams from "../../../../hooks/useProductParams";
import useMediaObjectCombinationRequest from "../../../../hooks/useMediaObjectCombinationRequest";
import { AbstractCase } from "../../../../types/Entities/AbstractCase";
import { Child } from "../../../../types/Entities/Child";

const RequestDocumentsPage = () => {
  const { productId } = useProductParams();
  const productApiUri = getProductData("divorce", "apiUri");
  const { data: divorce, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId)
  });

  if (isLoading) {
    return <LegalbirdLoader />;
  }
  return <RequestDocumentsPageInner divorce={divorce} />;
};

type RequestDocumentsPageInnerProps = {
  divorce: AbstractCase;
};

const RequestDocumentsPageInner = ({ divorce }: RequestDocumentsPageInnerProps) => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoadingInternal, setIsLoadingInternalInternal] = useState(false);

  const { productId } = useProductParams();
  const productApiUri = getProductData("divorce", "apiUri");

  let mediaObjectCombinationRequestTypes = [];

  if (divorce.marriageCertificate) {
    mediaObjectCombinationRequestTypes.push("marriageCertificate");
  }

  if (divorce.numberChildren > 0) {
    divorce.children.forEach((child: Child) => {
      if (child.birthCertificate === null) {
        mediaObjectCombinationRequestTypes.push("birthCertificateFiles" + child.id);
      }
    });
  }

  const { mediaObjectCombinationRequests, isLoadingMediaObjectCombinationRequest, updateAction } = useMediaObjectCombinationRequest(
    divorce,
    mediaObjectCombinationRequestTypes
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refreshDivorce = () => {
    queryClient.invalidateQueries({
      queryKey: [productApiUri, productId],
    });
  };

  const triggerUpdate = async () => {
    setIsLoadingInternalInternal(true);
    await ApiClient.post(`/generate_requested_mediaobjects/divorce/${divorce.id}`);
    navigate(getCaseLink(divorce));
  };

  useEffect(() => {
    if (divorce) {
      let hasRequestedDocument = divorce.marriageCertificate === null;
      _forEach(divorce.children, (child) => {
        hasRequestedDocument = hasRequestedDocument || child.birthCertificate === null;
      });
      if (!hasRequestedDocument) {
        navigate(getCaseLink(divorce));
      }
    }
  }, [divorce, navigate]);

  if (isLoadingMediaObjectCombinationRequest) {
    return (
      <Container sx={{ width: 250 }}>
        <LegalbirdLoader />
      </Container>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Fehlende Dokumente | Legalbird</title>
      </Helmet>
      <TextContainer>
        <HintBox headline={"Fehlende Dokumente ergänzen"}>Um Ihren Fall weiter bearbeiten zu können, benötigen wir noch folgende Unterlagen von Ihnen.</HintBox>
        <Paper elevation={4} sx={requestDocumentsPageStyle.box}>
          {divorce.marriageCertificate === null && (
            <FileUploadSection
              mediaObjectCombinationRequest={mediaObjectCombinationRequests.marriageCertificateFiles}
              temporaryMediaObjectsFieldname={"marriageCertificateFiles"}
              headline={"Heiratsurkunde"}
              helperText={
                "Bitte laden Sie hier Ihre Heiratsurkunde hoch. Sollte Ihnen keine deutsche Heiratsurkunde vorliegen, benötigen Sie eine Übersetzung."
              }
              productEntity={divorce}
              productEntityId={divorce.id}
              product={"divorce"}
              updateAction={updateAction}
              setIsBlocked={setIsBlocked}
              isBlocked={isBlocked}
              fieldName={"marriageCertificate"}
            />
          )}
          {_map(divorce.children, (child) => {
            if (child.birthCertificate !== null) {
              return null;
            }
            return (
              <FileUploadSection
                mediaObjectCombinationRequest={mediaObjectCombinationRequests["birthCertificateFiles" + child.id]}
                temporaryMediaObjectsFieldname={"birthCertificateFiles"}
                key={child.id}
                headline={"Geburtsurkunde " + child.name}
                helperText={"Bitte laden Sie hier die Geburtsurkunde hoch."}
                productEntity={child}
                productEntityId={divorce.id}
                product={"divorce"}
                updateAction={updateAction}
                setIsBlocked={setIsBlocked}
                isBlocked={isBlocked}
                postSubmitCallback={refreshDivorce}
                fieldName={"birthCertificate"}
              />
            );
          })}
          <br />
          <br />
          <ButtonLoading
            onClick={triggerUpdate}
            color="primary"
            variant="contained"
            disabled={false}
            fullWidth={true}
            type={"submit"}
            isLoading={isLoadingInternal}
          >
            Dokumente Hochladen
          </ButtonLoading>
        </Paper>
      </TextContainer>
    </Layout>
  );
};

export default RequestDocumentsPage;
